import { useContext } from 'react';
import { StringsContext } from '../lib/StringsContext';

export function getString(
  id: string,
  strings: ParsedStrings,
  placeholder?: boolean,
  values?: Record<string, string | number>,
  plain?: boolean,
) {
  if (!strings[id]) {
    console.warn(
      `Missing string ${id} in Sanity. Add it here: https://pinchos.sanity.studio/desk/translations;manage;bookingClient`,
    );
  }

  let string = placeholder
    ? id
    : (plain ? strings[id].value : strings[id]?.richText) || strings[id]?.value || id;

  // In the specific case of having forced placeholders via .env, just render the placeholder.
  // Useful for finding where occurences of a string might be in the code.
  if (process.env.NEXT_PUBLIC_FORCE_PLACEHOLDERS === 'true') {
    string = id;
  }

  // Loop through values and replace them in the string
  if (string?.includes('{{') && values && Object.keys(values).length) {
    Object.keys(values).forEach((key) => {
      string = string.replaceAll(`{{${key}}}`, values[key].toString());
    });
  }
  return { type: strings[id] && strings[id].richText ? 'richText' : 'string', value: string };
}

/**
 * @param {Object} props - The base props object
 * @param {String} props.id - The string ID, defined as `key` in Sanity
 * @param {Boolean} placeholder - Whether the String component instance is a placeholder, awaiting translation
 * @returns {String}
 */

interface StringProps {
  id: string;
  placeholder?: boolean;
  values?: Record<string, string>;
  plain?: boolean;
}

export const String = ({ id, placeholder = false, values, plain }: StringProps) => {
  const strings = useContext(StringsContext);
  if (!id) {
    throw Error('No prop `id` provided');
  }
  if (!placeholder && !strings[id]) {
    console.error(
      `No string found for id ${id}. Create it in Sanity with "Generic" or "Booking client" namespace here: https://pinchos.sanity.studio/intent/create/type=translations;template=translations/.`,
    );

    return <>{id}</>;
  }

  // In the specific case of having forced placeholders via .env, just render the placeholder.
  // Useful for finding where occurences of a string might be in the code.
  if (process.env.NEXT_PUBLIC_FORCE_PLACEHOLDERS === 'true') {
    return <>{id}</>;
  }

  const { type, value } = getString(id, strings, placeholder, values, plain);

  return type === 'richText' && !plain ? (
    <div dangerouslySetInnerHTML={{ __html: value }} />
  ) : (
    <>{value}</>
  );
};
